import { useMemo, useState } from 'react';
import type { Match, WinCondition } from '@edh/shared';
import {
  WIN_CONDITIONS,
  deckLabel,
  isRated,
  winConditionLabel,
} from '@edh/shared';
import { RankedAvatar } from './RankedAvatar';
import { MatchSummary } from './ResultSheet';
import { Sheet } from './Sheet';
import { useLookups, useStore } from '../lib/store';

/**
 * Every game, grouped by the night it was played.
 *
 * A night is the unit people remember games in — "the one where Urza went off
 * on turn four" belongs to a Tuesday, not to a timestamp. Grouping by calendar
 * day is close enough and needs nothing recorded that is not already there;
 * sessions exist in the data model for the same job and nothing creates them
 * yet, so this reads the dates instead of waiting for a feature.
 */
/** How many games a page holds. About a screen, which is the point. */
const PAGE_SIZE = 6;

export function History() {
  const { state, isLeader } = useStore();
  const { pilot, deck } = useLookups();
  const [viewing, setViewing] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  /**
   * A page at a time, newest first.
   *
   * Grouped after slicing rather than before, so a night that spans the break
   * gets its heading again at the top of the next page instead of the second
   * half arriving unlabelled.
   */
  const ordered = useMemo(
    () => [...state.matches].sort((a, b) => b.ts - a.ts),
    [state.matches],
  );
  const pages = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const start = current * PAGE_SIZE;
  const shownGames = ordered.slice(start, start + PAGE_SIZE);

  const nights = useMemo(() => {
    const byDay = new Map<string, Match[]>();
    for (const match of shownGames) {
      const day = new Date(match.ts).toDateString();
      const list = byDay.get(day);
      if (list) list.push(match);
      else byDay.set(day, [match]);
    }
    return [...byDay.entries()];
  }, [shownGames]);

  const newest = state.matches.reduce<Match | null>(
    (latest, m) => (latest === null || m.ts > latest.ts ? m : latest),
    null,
  );

  if (nights.length === 0) return null;

  const shown = state.matches.find((m) => m.id === viewing);
  const edited = state.matches.find((m) => m.id === editing);

  return (
    <>
      <div className="section-head">
        <h2>History</h2>
        <span className="hint">
          {state.matches.length} game{state.matches.length === 1 ? '' : 's'}
        </span>
      </div>

      {nights.map(([day, games]) => (
        <div className="night" key={day}>
          <div className="night-head">
            <span className="night-date">{formatNight(day)}</span>
            {/* The night's whole count, not the part of it on this page — the
                page break is the app's business, not the evening's. */}
            <span className="night-count">
              {countOn(ordered, day)} game{countOn(ordered, day) === 1 ? '' : 's'}
            </span>
          </div>

          {games.map((match) => {
            const winnerEntry = match.entries.find((e) => e.pilotId === match.winnerPilotId);
            const winner = match.winnerPilotId ? pilot(match.winnerPilotId) : undefined;
            /* Any of them, for a leader. An older game unwinds the league back
               to it and replays everything since, which is a heavier operation
               than undoing the last one but the same idea. */
            const editable = isLeader;

            return (
              <div className="night-game" key={match.id}>
                <button
                  className="night-game-open"
                  onClick={() => setViewing(match.id)}
                  aria-label={
                    isRated(match)
                      ? `open the game ${winner?.name ?? ''} won`
                      : 'open the cancelled game'
                  }
                >
                  {/* The rank they were that night. See `atMatch`. */}
                  <RankedAvatar pilot={winner} size={30} atMatch={match.id} />
                  <span className="night-game-what">
                    <span className="night-game-who">
                      {isRated(match) ? (winner?.name ?? 'Unknown') : 'Cancelled'}
                    </span>
                    {/* The name the app speaks, which is the table's name for
                        the deck where there is one. This row was reading the
                        commander off the card while every other screen said
                        "Korvold". */}
                    <span className="sub">
                      {(() => {
                        // A cancelled game has no winning deck to name, so the
                        // line says what happened instead of naming one.
                        if (!isRated(match)) return match.note || 'No result';
                        const d = winnerEntry ? deck(winnerEntry.deckId) : undefined;
                        return d ? deckLabel(d) : 'a deck';
                      })()}
                    </span>
                    {/* Its own line. Sharing one with the deck meant the two
                        competed for a width neither had, and the answer that
                        lost was always the same one — the deck name is longer,
                        so how the game ended was what got cut. */}
                    {/*
                      Format first, then how it ended. A drafted game is a
                      different achievement from a chosen one — you did not
                      pick the deck — and that is the first thing worth knowing
                      about a row somebody is scrolling past.
                    */}
                    <span className="night-game-how">
                      {match.mode === 'draft' ? 'Draft' : 'Standard'}
                      {match.winCondition && (
                        <>
                          <span className="dot"> · </span>
                          {winConditionLabel(match.winCondition)}
                        </>
                      )}
                    </span>
                  </span>
                  <span className="night-game-meta">
                    {match.entries.length}p{match.endTurn ? ` · T${match.endTurn}` : ''}
                  </span>
                </button>

                {editable && (
                  <button
                    className="night-game-edit"
                    onClick={() => setEditing(match.id)}
                    aria-label="edit this game"
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {pages > 1 && (
        <div className="pager">
          <button
            className="btn ghost"
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            ‹ Newer
          </button>
          <span className="pager-count num">
            {start + 1}–{start + shownGames.length} of {ordered.length}
          </span>
          <button
            className="btn ghost"
            disabled={current >= pages - 1}
            onClick={() => setPage(current + 1)}
          >
            Older ›
          </button>
        </div>
      )}

      {shown && (
        <Sheet label="Game summary" onClose={() => setViewing(null)}>
          <div className="result">
            <MatchSummary match={shown} />
          </div>
        </Sheet>
      )}

      {edited && (
        <EditGame
          match={edited}
          isNewest={newest?.id === edited.id}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

/**
 * Correcting a game.
 *
 * Ratings are sequential, so changing a result means every game after it was
 * scored against a league that never existed. Two ways to put that right, and
 * which one applies is decided by whether anything came after.
 *
 * The newest game is rewound and re-logged on the spot — the same pair of
 * operations the engine already does exactly, and it works offline because both
 * halves go through the queue. Anything older goes to the server, which unwinds
 * back to it and replays everything since. That one needs a connection and it
 * needs the real answer, so nothing is guessed locally first.
 *
 * Either way the measurements survive: the damage log, the odds frozen at
 * kick-off, who went first. The game that was played has not changed; only what
 * somebody wrote down about it has.
 */
function EditGame({
  match,
  isNewest,
  onClose,
}: {
  match: Match;
  isNewest: boolean;
  onClose: () => void;
}) {
  const { logMatch, undoLast, deleteMatch, editMatch, online } = useStore();
  const { pilot, deck } = useLookups();
  const [winner, setWinner] = useState(match.winnerPilotId);
  const [winCondition, setWinCondition] = useState<WinCondition | ''>(
    (match.winCondition as WinCondition | undefined) ?? '',
  );
  const [endTurn, setEndTurn] = useState(match.endTurn ? String(match.endTurn) : '');
  const [note, setNote] = useState(match.note ?? '');
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      const turns = Number.parseInt(endTurn, 10);
      const endTurnValue = Number.isFinite(turns) && turns > 0 ? turns : null;

      if (!isNewest) {
        await editMatch({
          matchId: match.id,
          winnerPilotId: winner ?? undefined,
          endTurn: endTurnValue,
          winCondition: winCondition || null,
          note: note.trim() || null,
        });
        onClose();
        return;
      }

      await undoLast();
      await logMatch({
        entries: match.entries,
        winnerPilotId: winner,
        // The night it happened, not the night somebody noticed.
        ts: match.ts,
        silent: true,
        sessionId: match.sessionId,
        endTurn: Number.isFinite(turns) && turns > 0 ? turns : undefined,
        onThePlay: match.onThePlay,
        mulligans: match.mulligans,
        winCondition: winCondition || undefined,
        lifeRemaining: match.lifeRemaining,
        // A different winner makes the recorded order a lie about this game, so
        // it is dropped rather than guessed at again.
        standing: winner === match.winnerPilotId ? match.standing : undefined,
        note: note.trim() || undefined,
        predicted: match.predicted,
        // Carried, not rebuilt. This path assembles the replacement field by
        // field, so anything not named here is dropped — which is how a deck
        // once lost its partner for weeks. Correcting a winner must not quietly
        // turn a drafted game into a standard one.
        mode: match.mode,
        damageLog: match.damageLog,
        poison: match.poison,
        poisonLog: match.poisonLog,
        commanderDamage: match.commanderDamage,
        durationSeconds: match.durationSeconds,
      });
      onClose();
    } finally {
      setBusy(false);
    }
  }

  /*
   * Undo for the newest game, a server-side unwind for anything older.
   *
   * Undo is a true inverse only while nothing has been scored on top, which is
   * exactly the newest game and nothing else. Older games go to `deleteMatch`,
   * which reverts back to the game and replays everything since - the same
   * shape as the correction above, and it needs a connection for the same
   * reason.
   */
  async function remove() {
    if (busy) return;
    setBusy(true);
    try {
      if (isNewest) await undoLast();
      else await deleteMatch(match.id);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet label="Edit game" onClose={onClose}>
      {/*
        A cancelled game has no result to correct - no winner, no rating moved,
        nothing a different answer here would change. All that is left to do
        with one is take it back out of the history.
      */}
      {!isRated(match) ? (
        <p className="hint" style={{ marginTop: 0 }}>
          This game was cancelled. Nobody won it and it moved nobody&apos;s rating, so there
          is no result to correct.
        </p>
      ) : (
        <>
        <div className="section-head" style={{ marginTop: 0 }}>
          <h2>Winner</h2>
        </div>
        <div className="list" style={{ marginBottom: 14 }}>
          {match.entries.map((entry) => {
            const p = pilot(entry.pilotId);
            const picked = winner === entry.pilotId;
            return (
              <button
                key={entry.pilotId}
                className="list-item"
                style={{
                  border: 0,
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  background: picked ? 'color-mix(in srgb, var(--gold) 16%, var(--panel))' : undefined,
                }}
                onClick={() => setWinner(entry.pilotId)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <RankedAvatar pilot={p} size={30} atMatch={match.id} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block' }}>{p?.name}</span>
                    <span className="sub">{(() => { const d = deck(entry.deckId); return d ? deckLabel(d) : ''; })()}</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <label className="field">
          <span>How it ended</span>
          <select
            className="select"
            value={winCondition}
            onChange={(e) => setWinCondition(e.target.value as WinCondition | '')}
          >
            <option value="">—</option>
            {WIN_CONDITIONS.map((value) => (
              <option key={value} value={value}>
                {winConditionLabel(value)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Ended on turn</span>
          <input
            className="input"
            type="number"
            inputMode="numeric"
            min={1}
            max={99}
            placeholder="—"
            value={endTurn}
            onChange={(e) => setEndTurn(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Note</span>
          <input
            className="input"
            placeholder="lost to mana screw"
            maxLength={140}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <p className="hint" style={{ marginBottom: 14 }}>
          {isNewest
            ? 'Saving rewinds this game and records it again, so the ratings land where they would have if it had been logged this way the first time.'
            : 'Saving rewinds the league back to this game and replays every one since. All of their ratings will move.'}
        </p>

        {!isNewest && !online && (
          <div className="banner">
            Rewriting an older game needs a connection — the replay happens on the server.
          </div>
        )}

        <div className="btn-row">
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={() => void save()}
            disabled={busy || (!isNewest && !online)}
          >
            {busy ? <span className="spinner" /> : 'Save'}
          </button>
        </div>
        </>
      )}

      {/* Any game, for a leader. Removing one from the middle changes how many
          placement games everybody has, which is why it goes through the server
          rather than the local undo: it unwinds back to the game and replays
          every rating after it. */}
      <>
        <div className="section-head">
          <h2>Remove</h2>
        </div>
          {confirmingDelete ? (
            <div className="btn-row">
              <button
                className="btn ghost"
                onClick={() => setConfirmingDelete(false)}
                disabled={busy}
              >
                Keep it
              </button>
              <button
                className="btn danger"
                onClick={() => void remove()}
                disabled={busy || (!isNewest && !online)}
              >
                Delete the game
              </button>
            </div>
          ) : (
            <button className="btn ghost block" onClick={() => setConfirmingDelete(true)}>
              Delete this game
            </button>
          )}
        {!isNewest && !online && (
          <p className="hint">
            Removing an older game replays every rating after it, so it needs a connection.
          </p>
        )}
      </>
    </Sheet>
  );
}

/** A pencil, drawn to match the tab icons rather than borrowed from a font. */
function PencilIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
      <path d="M14.5 6.5l3 3" />
    </svg>
  );
}

/** How many games that night held in total, regardless of where the page cuts. */
function countOn(all: readonly Match[], day: string): number {
  return all.filter((m) => new Date(m.ts).toDateString() === day).length;
}

/** "Tonight" and "Yesterday" beat a date for the two nights anyone is asking about. */
function formatNight(day: string): string {
  const date = new Date(day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - date.getTime()) / 86_400_000);
  if (diff === 0) return 'Tonight';
  if (diff === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
}
